// Big-number layer. Every economy value (HP, gold, damage, costs) is a Decimal:
// TT2-scale progression passes JS's ~1.8e308 double limit long before the endgame.
import Decimal, { type DecimalSource } from 'break_infinity.js';

export { Decimal };
export type { DecimalSource };

export const D = (v: DecimalSource): Decimal => new Decimal(v);
export const ZERO = D(0);
export const ONE = D(1);

const SHORT_UNITS = ['', 'K', 'M', 'B', 'T'];
// After T come two-letter units aa..zz (676 of them, up to ~1e2043), then scientific.
const LETTER_UNITS = 26 * 26;

/** Format for display: 999, 1.23K, 45.6M, 789aa, 1.23e2100. */
export function fmt(value: DecimalSource): string {
  const v = value instanceof Decimal ? value : D(value);
  if (!Number.isFinite(v.mantissa) || !Number.isFinite(v.exponent)) return '∞';
  if (v.lt(0)) return '-' + fmt(v.neg());
  if (v.lt(1000)) {
    const n = v.toNumber();
    return n < 10 && n % 1 ? n.toFixed(1) : Math.floor(n).toString();
  }
  const group = Math.floor(v.exponent / 3);
  const unitIndex = group - SHORT_UNITS.length;
  if (unitIndex >= LETTER_UNITS) {
    return `${v.mantissa.toFixed(2)}e${v.exponent}`;
  }
  const scaled = v.mantissa * Math.pow(10, v.exponent - group * 3);
  const unit = group < SHORT_UNITS.length
    ? SHORT_UNITS[group]
    : String.fromCharCode(97 + Math.floor(unitIndex / 26)) + String.fromCharCode(97 + (unitIndex % 26));
  // toFixed can round 999.95 up to "1000.0"; floor keeps the 3-digit cap.
  const digits = scaled < 10 ? scaled.toFixed(2) : scaled < 100 ? scaled.toFixed(1) : Math.floor(scaled).toString();
  return digits + unit;
}

/** Decimal serialization for saves (string form survives JSON losslessly). */
export const toSave = (d: Decimal): string => d.toString();
export const fromSave = (s: unknown, fallback: DecimalSource = 0): Decimal => {
  if (typeof s === 'number' || typeof s === 'string') {
    const d = D(s);
    if (Number.isFinite(d.mantissa) && Number.isFinite(d.exponent)) return d;
  }
  return D(fallback);
};
